import React, { useState, useRef, useEffect } from 'react';
import { 
  TextInput, 
  Paper, 
  Text, 
  Group, 
  Badge, 
  Switch, 
  ActionIcon, 
  Divider, 
  Tooltip,
  Box,
  Loader,
  Button
} from '@mantine/core';
import { 
  IconSearch, 
  IconShield, 
  IconInfoCircle,
  IconX,
  IconRefresh
} from '@tabler/icons-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCharacters } from '../hooks/useCharacters';
import { Character } from '../api/characters';
import { useCharacterContext } from '../contexts/CharacterContext';

/**
 * Character List component
 * Displays a virtualized list of characters with search and filtering options
 */
const CharacterList: React.FC = () => {
  // Get selected character from context
  const { selectedCharacter, setSelectedCharacter } = useCharacterContext();
  
  // Search and filter state
  const [search, setSearch] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(true);
  const [adminOnly, setAdminOnly] = useState(false);
  
  // Debounced search state
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Virtualization container ref
  const parentRef = useRef<HTMLDivElement>(null);
  
  // State to track when to refresh characters
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(true);
  
  // Fetch characters with search and filters
  const { 
    characters, 
    isLoading, 
    isError, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage,
    refetch,
    isFetching
  } = useCharacters({
    search: debouncedSearch,
    online: onlineOnly,
    admin: adminOnly
  });
  
  // Setup automatic refresh interval (every 30 seconds)
  useEffect(() => {
    if (!isAutoRefreshing) return;
    
    const intervalId = setInterval(() => {
      refetch();
      setRefreshTrigger(prev => prev + 1);
    }, 30000); // 30 seconds
    
    return () => clearInterval(intervalId);
  }, [refetch, isAutoRefreshing]);
  
  // Setup virtualization
  const virtualizer = useVirtualizer({
    count: characters.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 75,
    overscan: 5,
  });
  
  // Update debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    
    return () => {
      clearTimeout(handler);
    };
  }, [search]);
  
  // Handle infinite scrolling
  const lastVirtualItem = virtualizer.getVirtualItems().at(-1);
  
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    
    if (
      lastVirtualItem &&
      lastVirtualItem.index >= characters.length - 1 - virtualizer.options.overscan
    ) {
      fetchNextPage();
    }
  }, [
    hasNextPage, 
    isFetchingNextPage, 
    lastVirtualItem, 
    characters.length,
    fetchNextPage,
    virtualizer.options.overscan
  ]);
  
  // Manual refresh function
  const handleManualRefresh = () => {
    refetch();
    setRefreshTrigger(prev => prev + 1);
  };
  
  // Toggle auto-refresh
  const toggleAutoRefresh = () => {
    setIsAutoRefreshing(prev => !prev);
  };
  
  // Render character item
  const renderCharacterItem = (character: Character) => {
    const isSelected = selectedCharacter?.id === character.id;
    const isOnline = character.is_online === 1;
    const isAdmin = character.is_admin > 0;
    
    // Format login time
    const formatLoginTime = (loginTime: string | null) => {
      if (!loginTime) return 'Never logged in';
      const date = new Date(loginTime);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      
      if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffMinutes > 0) {
        return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
      } else {
        return 'Just now';
      }
    };
    
    return (
      <div 
        key={character.id}
        className={`p-2 cursor-pointer transition-colors ${
          isSelected 
            ? 'bg-blue-100' 
            : 'hover:bg-gray-50'
        }`}
        onClick={() => setSelectedCharacter(character)}
      >
        <Group justify="space-between" wrap="nowrap">
          <div className="flex-1 min-w-0">
            <Text fw={isSelected ? 700 : 500} size="sm" truncate>
              {character.name}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {character.class_name} (Lvl {character.level})
            </Text>
            <Text size="xs" c="dimmed" truncate>
              Last login: {formatLoginTime(character.login_time)}
            </Text>
          </div>
          
          <Group gap="xs">
            {isOnline && <Badge color="green" size="sm">Online</Badge>}
            {isAdmin && (
              <Tooltip label="Admin">
                <ActionIcon size="sm" color="yellow">
                  <IconShield size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip
              label={
                <div>
                  <Text size="xs">Race: {character.race}</Text>
                  <Text size="xs">Sex: {character.sex === 0 ? 'Male' : 'Female'}</Text>
                  <Text size="xs">ID: {character.id}</Text>
                  <Text size="xs">Created: {new Date(character.create_time).toLocaleString()}</Text>
                  <Text size="xs">Last Login: {character.login_time ? new Date(character.login_time).toLocaleString() : 'Never'}</Text>
                </div>
              }
              w={200}
              multiline
            >
              <ActionIcon size="sm">
                <IconInfoCircle size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </div>
    );
  };
  
  // Calculate content height based on state
  const getContentHeight = () => {
    if (isLoading && !characters.length) return "h-full flex-grow";
    if (isError) return "h-full flex-grow";
    if (characters.length === 0) return "h-full flex-grow";
    return "flex-grow";
  };
  
  return (
    <Paper shadow="xs" h="100%" radius={0} p="md" withBorder className="h-full flex flex-col">
      <Group justify="space-between" mb="xs">
        <Text fw={700}>Characters</Text>
        <Group gap="xs">
          <Tooltip label={isAutoRefreshing ? "Auto-refresh on (30s)" : "Auto-refresh off"}>
            <ActionIcon 
              color={isAutoRefreshing ? "green" : "gray"} 
              variant="subtle"
              onClick={toggleAutoRefresh}
              size="sm"
            >
              <IconRefresh size={18} className={isAutoRefreshing ? "animate-spin-slow" : ""} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Refresh now">
            <ActionIcon 
              onClick={handleManualRefresh} 
              loading={isFetching && !isFetchingNextPage}
              size="sm"
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      
      {/* Search and filter controls */}
      <TextInput
        placeholder="Search characters..."
        leftSection={<IconSearch size={16} />}
        rightSection={
          search ? (
            <ActionIcon onClick={() => setSearch('')} size="sm" variant="subtle">
              <IconX size={16} />
            </ActionIcon>
          ) : null
        }
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        mb="xs"
      />
      
      <Group justify="space-between" mb="md">
        <Switch 
          label="Online only" 
          checked={onlineOnly} 
          onChange={(e) => setOnlineOnly(e.currentTarget.checked)}
          size="xs"
        />
        <Switch 
          label="Admins only" 
          checked={adminOnly} 
          onChange={(e) => setAdminOnly(e.currentTarget.checked)}
          size="xs"
        />
      </Group>
      
      <Divider mb="md" />
      
      {/* Virtualized list */}
      {isLoading && !characters.length ? (
        <Box className="flex items-center justify-center flex-grow">
          <Loader />
        </Box>
      ) : isError ? (
        <Box className="flex items-center justify-center flex-grow">
          <Text c="red">Failed to load characters</Text>
        </Box>
      ) : characters.length === 0 ? (
        <Box className="flex items-center justify-center flex-grow">
          <Text c="dimmed">No characters found</Text>
        </Box>
      ) : (
        <div
          ref={parentRef}
          className="overflow-auto flex-grow"
        >
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => (
              <div
                key={virtualItem.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {renderCharacterItem(characters[virtualItem.index])}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Load more button */}
      {hasNextPage && (
        <Box mt="sm" className="flex justify-center">
          <Button
            size="xs"
            variant="light"
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
            disabled={isFetchingNextPage}
          >
            Load more
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default CharacterList; 